import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import { 
  FiSearch, FiUser, FiUsers, FiMail, FiPhone, 
  FiRefreshCw, FiFilter, FiX,
  FiEye, FiUserCheck, FiTrash2, FiPlus, FiDownload, FiUserX, FiUnlock,
  FiAlertCircle, FiChevronDown, FiChevronUp, FiCheckCircle,
  FiWifi, FiWifiOff, FiGrid, FiList, FiCalendar, FiBarChart2,
  FiPieChart, FiTrendingUp, FiSettings
} from 'react-icons/fi';
import CreateUserModal from './CreateUserModal';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Formater les dates relatives
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  if (days < 30) return `Il y a ${Math.floor(days / 7)} sem`;
  if (days < 365) return `Il y a ${Math.floor(days / 30)} mois`;
  return `Il y a ${Math.floor(days / 365)} an(s)`;
};

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('all'); // all, today, week, month, year
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState(null);
  const [banUserId, setBanUserId] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [showMissionCreditsModal, setShowMissionCreditsModal] = useState(false);
  const [missionCreditsUser, setMissionCreditsUser] = useState(null);
  const [missionCreditsAmount, setMissionCreditsAmount] = useState('1');
  const [missionCreditsLoading, setMissionCreditsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showStats] = useState(true);
  const [showCharts] = useState(true);
  const itemsPerPage = 20;
  const queryClient = useQueryClient();

  const fetchAllUsers = useCallback(async () => {
    let page = 1;
    let aggregated = [];
    let totalPages = 1;
    const maxPages = 50;
    do {
      const response = await api.getAllUsers({ page, limit: 100 });
      if (!response?.success) {
        throw new Error(response?.message || 'Erreur lors du chargement des utilisateurs');
      }
      aggregated = aggregated.concat(response.data || []);
      totalPages = response.pagination?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages && page <= maxPages);
    return aggregated;
  }, []);

  const {
    data: usersQueryData,
    isLoading: usersQueryLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchAllUsers,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (usersQueryData) {
      setUsers(usersQueryData);
    }
  }, [usersQueryData]);

  useEffect(() => {
    setLoading(usersQueryLoading);
  }, [usersQueryLoading]);

  /** Crédits missions : liste GET /users (snake_case) + secours endpoint présence (camelCase), ids normalisés en string */
  const missionCreditsForUser = (user) => {
    const cu = connectedUsers.find((c) => String(c.id) === String(user.id));
    const raw =
      user.mission_credits_balance ??
      user.missionCreditsBalance ??
      cu?.missionCreditsBalance;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  /** Missions au statut « completed » (quotes.mover_id + service_requests.demenageur_id) */
  const missionsCountForUser = (user) => {
    const cu = connectedUsers.find((c) => String(c.id) === String(user.id));
    const raw = user.missions_count ?? user.missionsCount ?? cu?.missionsCount;
    if (raw === undefined || raw === null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const loadUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    await refetchUsers();
  };

  useEffect(() => {
    loadConnectedUsers();

    socketService.connect().catch(() => {});
    const unsubscribeConnect = socketService.on('connect', () => {
      // Après reconnexion, resynchroniser la liste complète une seule fois.
      loadConnectedUsers();
    });
    const unsubscribePresence = socketService.on('admin:user_presence', (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      const isOnline = !!payload?.isOnline;
      setConnectedUsers((prev) => {
        const idx = prev.findIndex((u) => String(u.id) === String(userId));
        if (idx === -1) {
          return prev;
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          isOnline,
          connectionStatus: isOnline ? 'online' : 'offline',
        };
        return next;
      });
    });

    return () => {
      unsubscribeConnect?.();
      unsubscribePresence?.();
    };
  }, []);

  const loadConnectedUsers = async () => {
    try {
      const response = await api.getUsersWithConnectionStatus();
      if (response && response.success && response.data) {
        setConnectedUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs connectés:', error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'created_at' || sortField === 'updated_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const filteredUsers = sortedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower);
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    const connectedUser = connectedUsers.find((cu) => String(cu.id) === String(user.id));
    const isOnline = connectedUser?.isOnline || false;
    
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'online' && isOnline) ||
      (filterStatus === 'offline' && !isOnline) ||
      (filterStatus === 'banned' && user.is_banned) ||
      (filterStatus === 'active' && !user.is_banned);
    
    // Filtre par date
    let matchesDate = true;
    if (filterDate !== 'all' && user.created_at) {
      const userDate = new Date(user.created_at);
      const now = new Date();
      const diff = now - userDate;
      const days = Math.floor(diff / 86400000);
      
      if (filterDate === 'today' && days > 0) matchesDate = false;
      else if (filterDate === 'week' && days > 7) matchesDate = false;
      else if (filterDate === 'month' && days > 30) matchesDate = false;
      else if (filterDate === 'year' && days > 365) matchesDate = false;
    }
    
    return matchesSearch && matchesRole && matchesStatus && matchesDate;
  });

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === paginatedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(paginatedUsers.map(u => u.id));
    }
  };

  const handleBanUser = async (userId) => {
    setBanUserId(userId);
    setShowBanModal(true);
  };

  const confirmBan = async () => {
    if (!banUserId) return;

    try {
      setLoading(true);
      setShowBanModal(false);
      const response = await api.banUser(banUserId, banReason || 'Raison non spécifiée');
      if (response && response.success) {
        setSuccessMessage('Utilisateur banni avec succès');
        setTimeout(() => setSuccessMessage(null), 3000);
        setBanReason('');
        setBanUserId(null);
        loadUsers();
      } else {
        setError(response?.message || 'Erreur lors du bannissement');
      }
    } catch (err) {
      console.error('Erreur ban:', err);
      setError(err.message || 'Erreur lors du bannissement');
    } finally {
      setLoading(false);
    }
  };

  const openMissionCreditsModal = (user) => {
    setMissionCreditsUser(user);
    setMissionCreditsAmount('1');
    setShowMissionCreditsModal(true);
  };

  const closeMissionCreditsModal = () => {
    setShowMissionCreditsModal(false);
    setMissionCreditsUser(null);
    setMissionCreditsAmount('1');
  };

  const confirmAddMissionCredits = async () => {
    if (!missionCreditsUser?.id) return;
    const n = parseInt(String(missionCreditsAmount).trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > 999) {
      setError('Nombre de crédits invalide (1 à 999).');
      return;
    }
    try {
      setMissionCreditsLoading(true);
      setError(null);
      const response = await api.addMoverMissionCredits(missionCreditsUser.id, n);
      if (response && response.success) {
        setSuccessMessage(response.message || `${n} crédit(s) ajouté(s)`);
        setTimeout(() => setSuccessMessage(null), 4000);
        closeMissionCreditsModal();
        loadUsers();
      } else {
        setError(response?.message || "Erreur lors de l'ajout des crédits");
      }
    } catch (err) {
      setError(err.message || "Erreur lors de l'ajout des crédits");
    } finally {
      setMissionCreditsLoading(false);
    }
  };

  const handleUnbanUser = async (userId) => {
    setConfirmModalConfig({
      title: 'Débannir l\'utilisateur',
      message: 'Êtes-vous sûr de vouloir débannir cet utilisateur ?',
      icon: <FiUnlock className="w-8 h-8 text-green-400" />,
      confirmText: 'Débannir',
      cancelText: 'Annuler',
      confirmColor: 'green',
      onConfirm: async () => {
        setShowConfirmModal(false);
    try {
      setLoading(true);
      const response = await api.unbanUser(userId);
      if (response && response.success) {
            setSuccessMessage('Utilisateur débanni avec succès');
            setTimeout(() => setSuccessMessage(null), 3000);
        loadUsers();
      } else {
            setError(response?.message || 'Erreur lors du débannissement');
      }
    } catch (err) {
      console.error('Erreur unban:', err);
          setError(err.message || 'Erreur lors du débannissement');
    } finally {
      setLoading(false);
    }
      }
    });
    setShowConfirmModal(true);
  };

  const handleBulkBan = async () => {
    setConfirmModalConfig({
      title: 'Bannir des utilisateurs',
      message: `Êtes-vous sûr de vouloir bannir ${selectedUsers.length} utilisateur(s) ?`,
      icon: <FiUserX className="w-8 h-8 text-orange-400" />,
      confirmText: 'Bannir',
      cancelText: 'Annuler',
      confirmColor: 'orange',
      onConfirm: async () => {
        setShowConfirmModal(false);
        setShowBulkActionsModal(false);
        try {
          setLoading(true);
          // Bannir chaque utilisateur
          for (const userId of selectedUsers) {
            await api.banUser(userId, 'Bannissement en masse');
          }
          setSuccessMessage(`${selectedUsers.length} utilisateur(s) banni(s) avec succès`);
          setTimeout(() => setSuccessMessage(null), 3000);
          setSelectedUsers([]);
          loadUsers();
        } catch (err) {
          console.error('Erreur bulk ban:', err);
          setError(err.message || 'Erreur lors du bannissement en masse');
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleBulkUnban = async () => {
    setConfirmModalConfig({
      title: 'Débannir des utilisateurs',
      message: `Êtes-vous sûr de vouloir débannir ${selectedUsers.length} utilisateur(s) ?`,
      icon: <FiUnlock className="w-8 h-8 text-green-400" />,
      confirmText: 'Débannir',
      cancelText: 'Annuler',
      confirmColor: 'green',
      onConfirm: async () => {
        setShowConfirmModal(false);
        setShowBulkActionsModal(false);
        try {
          setLoading(true);
          for (const userId of selectedUsers) {
            await api.unbanUser(userId);
          }
          setSuccessMessage(`${selectedUsers.length} utilisateur(s) débanni(s) avec succès`);
          setTimeout(() => setSuccessMessage(null), 3000);
          setSelectedUsers([]);
          loadUsers();
        } catch (err) {
          console.error('Erreur bulk unban:', err);
          setError(err.message || 'Erreur lors du débannissement en masse');
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteUsers = async (userIds) => {
    setConfirmModalConfig({
      title: 'Supprimer des utilisateurs',
      message: `Êtes-vous sûr de vouloir supprimer ${userIds.length} utilisateur(s) ? Cette action est irréversible.`,
      icon: <FiTrash2 className="w-8 h-8 text-red-400" />,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmColor: 'red',
      onConfirm: async () => {
        setShowConfirmModal(false);
        try {
          setLoading(true);
      if (userIds.length === 1) {
        const response = await api.deleteUser(userIds[0]);
        if (!response || !response.success) {
          throw new Error(response?.message || 'Erreur lors de la suppression');
        }
      } else {
        const response = await api.deleteUsers(userIds);
        if (!response || !response.success) {
          throw new Error(response?.message || 'Erreur lors de la suppression');
        }
      }
          setSuccessMessage(`${userIds.length} utilisateur(s) supprimé(s) avec succès`);
          setTimeout(() => setSuccessMessage(null), 3000);
      setSelectedUsers([]);
      loadUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError(error.message || 'Erreur lors de la suppression');
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = async () => {
    setConfirmModalConfig({
      title: 'Supprimer des utilisateurs',
      message: `Êtes-vous sûr de vouloir supprimer ${selectedUsers.length} utilisateur(s) ? Cette action est irréversible.`,
      icon: <FiTrash2 className="w-8 h-8 text-red-400" />,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      confirmColor: 'red',
      onConfirm: async () => {
        setShowConfirmModal(false);
        setShowBulkActionsModal(false);
        try {
          setLoading(true);
          const response = await api.deleteUsers(selectedUsers);
          if (!response || !response.success) {
            throw new Error(response?.message || 'Erreur lors de la suppression');
          }
          setSuccessMessage(`${selectedUsers.length} utilisateur(s) supprimé(s) avec succès`);
          setTimeout(() => setSuccessMessage(null), 3000);
          setSelectedUsers([]);
          loadUsers();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          setError(error.message || 'Erreur lors de la suppression');
        } finally {
          setLoading(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleExportCSV = () => {
    const headers = [
      'ID', 'Prénom', 'Nom', 'Email', 'Téléphone', 'Rôle', 'Type déménageur',
      'Statut', 'En ligne', 'Connexion via', 'Banni', 'Date d\'inscription', 'Dernière connexion'
    ];
    const csvContent = [
      headers.join(','),
      ...filteredUsers.map(user => {
        const connectedUser = connectedUsers.find((cu) => String(cu.id) === String(user.id));
        const isOnline = connectedUser?.isOnline || false;
        return [
        user.id,
        `"${user.first_name || ''}"`,
        `"${user.last_name || ''}"`,
        user.email || '',
        user.phone || '',
        user.role || '',
          getMoverServiceTypeLabel(getMoverServiceType(user, connectedUser)) || '',
          user.is_banned ? 'Banni' : 'Actif',
          isOnline ? 'Oui' : 'Non',
          isOnline ? (getConnectedViaLabel(connectedUser) || '') : '',
          user.is_banned ? 'Oui' : 'Non',
          formatDate(user.created_at),
          connectedUser?.lastSeen ? formatDate(connectedUser.lastSeen) : 'N/A'
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getRoleBadge = (role) => {
    const badges = {
      client: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      demenageur: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return badges[role] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getRoleLabel = (role) => {
    const labels = {
      client: 'Client',
      demenageur: 'Déménageur',
      mover: 'Déménageur',
      admin: 'Administrateur'
    };
    return labels[role] || role;
  };

  const getMoverServiceType = (user, connectedUser) => {
    const raw =
      user?.mover_service_type ??
      user?.moverServiceType ??
      connectedUser?.moverServiceType;
    const norm = String(raw || '').trim().toLowerCase();
    return norm === 'local' || norm === 'international' ? norm : null;
  };

  const getMoverServiceTypeLabel = (type) => {
    if (type === 'local') return 'Local';
    if (type === 'international') return 'International';
    return null;
  };

  const getMoverServiceTypeBadge = (type) => {
    if (type === 'local') return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    if (type === 'international') return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getConnectedViaLabel = (connectedUser) => {
    const via = connectedUser?.connectedVia ?? connectedUser?.connected_via;
    if (!via) return null;
    if (via === 'app') return 'App';
    if (via === 'web') return 'Web';
    if (via === 'both') return 'App + Web';
    return null;
  };

  const getConnectedViaBadge = (via) => {
    if (via === 'app') return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
    if (via === 'web') return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
    return 'bg-violet-500/20 text-violet-300 border-violet-500/30';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />;
  };

  // Statistiques
  const stats = {
    total: users.length,
    clients: users.filter(u => u.role === 'client').length,
    demenageurs: users.filter(u => u.role === 'demenageur').length,
    admins: users.filter(u => u.role === 'admin' || u.is_admin).length,
    online: connectedUsers.filter(u => u.isOnline).length,
    offline: connectedUsers.filter(u => !u.isOnline).length,
    banned: users.filter(u => u.is_banned).length,
    active: users.filter(u => !u.is_banned).length,
    newToday: users.filter(u => {
      if (!u.created_at) return false;
      const userDate = new Date(u.created_at);
      const today = new Date();
      return userDate.toDateString() === today.toDateString();
    }).length,
    newThisWeek: users.filter(u => {
      if (!u.created_at) return false;
      const userDate = new Date(u.created_at);
      const now = new Date();
      const diff = now - userDate;
      return Math.floor(diff / 86400000) <= 7;
    }).length,
    newThisMonth: users.filter(u => {
      if (!u.created_at) return false;
      const userDate = new Date(u.created_at);
      const now = new Date();
      const diff = now - userDate;
      return Math.floor(diff / 86400000) <= 30;
    }).length
  };

  // Données pour les graphiques
  const roleDistributionData = {
    labels: ['Clients', 'Déménageurs', 'Administrateurs'],
    datasets: [{
      label: 'Répartition par rôle',
      data: [stats.clients, stats.demenageurs, stats.admins],
      backgroundColor: [
        'rgba(59, 130, 246, 0.5)',
        'rgba(168, 85, 247, 0.5)',
        'rgba(234, 179, 8, 0.5)'
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(168, 85, 247, 1)',
        'rgba(234, 179, 8, 1)'
      ],
      borderWidth: 2
    }]
  };

  const statusDistributionData = {
    labels: ['Actifs', 'Bannis', 'En ligne', 'Hors ligne'],
    datasets: [{
      label: 'Statut',
      data: [stats.active, stats.banned, stats.online, stats.offline],
      backgroundColor: [
        'rgba(34, 197, 94, 0.5)',
        'rgba(239, 68, 68, 0.5)',
        'rgba(16, 185, 129, 0.5)',
        'rgba(107, 114, 128, 0.5)'
      ],
      borderColor: [
        'rgba(34, 197, 94, 1)',
        'rgba(239, 68, 68, 1)',
        'rgba(16, 185, 129, 1)',
        'rgba(107, 114, 128, 1)'
      ],
      borderWidth: 2
    }]
  };

  // Calculer les nouveaux utilisateurs par jour (derniers 30 jours)
  const getUsersByDay = () => {
    const days = [];
    const counts = [];
    const now = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      days.push(dateStr);
      
      const count = users.filter(u => {
        if (!u.created_at) return false;
        const userDate = new Date(u.created_at);
        return userDate.toDateString() === date.toDateString();
      }).length;
      counts.push(count);
    }
    
    return { days, counts };
  };

  const { days: registrationDays, counts: registrationCounts } = getUsersByDay();
  
  const registrationTrendData = {
    labels: registrationDays,
    datasets: [{
      label: 'Nouveaux utilisateurs',
      data: registrationCounts,
      borderColor: 'rgba(234, 179, 8, 1)',
      backgroundColor: 'rgba(234, 179, 8, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#e5e7eb' },
        position: 'bottom'
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(107, 114, 128, 0.2)' },
        ticks: { color: '#9ca3af' }
      },
      y: {
        grid: { color: 'rgba(107, 114, 128, 0.2)' },
        ticks: { color: '#9ca3af', beginAtZero: true }
      }
    }
  };

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiUsers className="w-8 h-8 mr-3" />
            Gestion des utilisateurs
          </h1>
          <p className="text-gray-400">Gérez tous les utilisateurs de la plateforme</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
            title={viewMode === 'list' ? 'Vue Grille' : 'Vue Liste'}
          >
            {viewMode === 'list' ? <FiGrid className="w-5 h-5" /> : <FiList className="w-5 h-5" />}
          </button>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
          >
            <FiDownload className="mr-2" />
            Exporter CSV
          </button>
          <button
            onClick={() => {
              loadUsers();
              loadConnectedUsers();
            }}
            disabled={loading}
            className="px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center"
          >
            <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center shadow-lg"
          >
            <FiPlus className="mr-2" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Messages d'alerte/succès */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <FiX className="w-4 h-4" />
            </button>
          </div>
      )}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiCheckCircle className="w-5 h-5 mr-2" />
            {successMessage}
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-300">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards améliorées */}
      {showStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">Total</p>
                <div className="p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                  <FiUsers className="w-5 h-5 text-yellow-400" />
          </div>
        </div>
              <p className="text-3xl font-bold text-yellow-400">{stats.total}</p>
          </div>
            <div className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 border border-blue-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">Clients</p>
                <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <FiUserCheck className="w-5 h-5 text-blue-400" />
        </div>
          </div>
              <p className="text-3xl font-bold text-blue-400">{stats.clients}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.total > 0 ? Math.round((stats.clients / stats.total) * 100) : 0}%
          </p>
        </div>
            <div className="bg-gradient-to-br from-purple-600/10 to-purple-800/10 border border-purple-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">Déménageurs</p>
                <div className="p-2 bg-purple-500/20 rounded-lg border border-purple-500/30">
                  <FiUserCheck className="w-5 h-5 text-purple-400" />
          </div>
              </div>
              <p className="text-3xl font-bold text-purple-400">{stats.demenageurs}</p>
              <p className="text-xs text-gray-500 mt-1">
                {stats.total > 0 ? Math.round((stats.demenageurs / stats.total) * 100) : 0}%
          </p>
        </div>
            <div className="bg-gradient-to-br from-green-600/10 to-green-800/10 border border-green-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">En ligne</p>
                <div className="p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                  <FiWifi className="w-5 h-5 text-green-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-green-400">{stats.online}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.offline} hors ligne</p>
            </div>
            <div className="bg-gradient-to-br from-red-600/10 to-red-800/10 border border-red-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">Bannis</p>
                <div className="p-2 bg-red-500/20 rounded-lg border border-red-500/30">
                  <FiUserX className="w-5 h-5 text-red-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-red-400">{stats.banned}</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-600/10 to-indigo-800/10 border border-indigo-600/30 rounded-xl p-4 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-400 uppercase">Nouveaux (mois)</p>
                <div className="p-2 bg-indigo-500/20 rounded-lg border border-indigo-500/30">
                  <FiTrendingUp className="w-5 h-5 text-indigo-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-indigo-400">{stats.newThisMonth}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.newThisWeek} cette semaine</p>
        </div>
      </div>

          {/* Graphiques */}
          {showCharts && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FiPieChart className="w-5 h-5 mr-2 text-yellow-400" />
                  Répartition par rôle
                </h3>
                <div className="h-64">
                  <Doughnut data={roleDistributionData} options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: { ...chartOptions.plugins.legend, position: 'bottom' }
                    }
                  }} />
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FiBarChart2 className="w-5 h-5 mr-2 text-yellow-400" />
                  Répartition par statut
                </h3>
                <div className="h-64">
                  <Bar data={statusDistributionData} options={chartOptions} />
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FiTrendingUp className="w-5 h-5 mr-2 text-yellow-400" />
                  Tendance d'inscription (30 jours)
                </h3>
                <div className="h-64">
                  <Line data={registrationTrendData} options={chartOptions} />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Actions en masse améliorées */}
      {selectedUsers.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-600/30 rounded-xl p-5 flex items-center justify-between shadow-lg">
          <span className="text-base font-semibold text-yellow-400 flex items-center">
            <FiCheckCircle className="w-5 h-5 mr-2" />
            {selectedUsers.length} utilisateur(s) sélectionné(s)
          </span>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkActionsModal(true)}
              className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-medium text-sm flex items-center shadow-md"
            >
              <FiSettings className="mr-2" />
              Actions en masse
            </button>
            <button
              onClick={() => handleDeleteUsers(selectedUsers)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm flex items-center shadow-md"
            >
              <FiTrash2 className="mr-2" />
              Supprimer
            </button>
            <button
              onClick={() => setSelectedUsers([])}
              className="px-4 py-2 bg-gray-700 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search améliorés */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-lg">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-3">
            <FiFilter className="text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Tous les rôles</option>
              <option value="client">Client</option>
              <option value="demenageur">Déménageur</option>
              <option value="admin">Administrateur</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
              <option value="active">Actifs</option>
              <option value="banned">Bannis</option>
            </select>
            <select
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table améliorée */}
      {viewMode === 'list' ? (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl overflow-hidden shadow-xl max-w-[98%] mx-auto">
        <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900/50 border-b border-gray-700">
                  <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === paginatedUsers.length && paginatedUsers.length > 0}
                    onChange={handleSelectAll}
                      className="w-5 h-5 text-yellow-600 border-gray-600 rounded focus:ring-yellow-500 bg-gray-800 cursor-pointer"
                  />
                </th>
                <th 
                    className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                  onClick={() => handleSort('first_name')}
                >
                    <div className="flex items-center space-x-2">
                    <span>Utilisateur</span>
                    <SortIcon field="first_name" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                  onClick={() => handleSort('role')}
                >
                    <div className="flex items-center space-x-2">
                    <span>Rôle</span>
                    <SortIcon field="role" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Solde / missions terminées
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-yellow-400 transition-colors"
                  onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Date d'inscription</span>
                      <SortIcon field="created_at" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
              <tbody className="divide-y divide-gray-700/50">
              {loading ? (
                <tr>
                    <td colSpan="8" className="px-4 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700 border-t-yellow-500"></div>
                        <p className="ml-4 text-yellow-400">Chargement...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                    <td colSpan="8" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <FiUser className="w-16 h-16 text-gray-600 mb-4" />
                        <p className="text-gray-400 font-medium">Aucun utilisateur trouvé</p>
                      </div>
                  </td>
                </tr>
              ) : (
                  paginatedUsers.map((user) => {
                    const connectedUser = connectedUsers.find((cu) => String(cu.id) === String(user.id));
                    const isOnline = connectedUser?.isOnline || false;
                    
                    return (
                  <tr 
                    key={user.id} 
                      className={`hover:bg-gray-700/30 transition-colors ${
                        selectedUsers.includes(user.id) ? 'bg-gray-700/20' : ''
                    }`}
                  >
                      <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                            className="w-5 h-5 text-yellow-600 border-gray-600 rounded focus:ring-yellow-500 bg-gray-800 cursor-pointer"
                      />
                    </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 border-2 border-gray-800 flex items-center justify-center text-white font-bold text-xs shadow-lg">
                            {getInitials(user.first_name, user.last_name)}
                          </div>
                        </div>
                        <div className="ml-4">
                              <div className="text-sm font-bold text-white">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-400">{user.email}</div>
                        </div>
                      </div>
                    </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-white flex items-center">
                        <FiMail className="mr-2 text-gray-400" />
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="text-sm text-gray-400 flex items-center mt-1">
                          <FiPhone className="mr-2 text-gray-500" />
                          {user.phone}
                        </div>
                      )}
                    </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full border ${getRoleBadge(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                          {(user.role === 'demenageur' || user.role === 'mover') && (() => {
                            const serviceType = getMoverServiceType(user, connectedUser);
                            const serviceLabel = getMoverServiceTypeLabel(serviceType);
                            if (!serviceLabel) return null;
                            return (
                              <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full border ${getMoverServiceTypeBadge(serviceType)}`}>
                                {serviceLabel}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {user.role === 'demenageur' || user.role === 'mover' ? (
                          <div>
                            <p className="text-emerald-400 font-semibold">
                              Solde : <span className="text-white">{missionCreditsForUser(user)}</span>
                            </p>
                            <p className="text-gray-400 text-xs mt-0.5">
                              Missions terminées : <span className="text-gray-200">{missionsCountForUser(user)}</span>
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                        <div className="flex items-center">
                          <FiCalendar className="mr-2 text-gray-500" />
                          {formatRelativeDate(user.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {isOnline ? (
                            <span className="px-2 py-1 inline-flex items-center text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                              En ligne
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex items-center text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
                              <FiWifiOff className="w-3 h-3 mr-2" />
                              Hors ligne
                            </span>
                          )}
                          {isOnline && (() => {
                            const viaLabel = getConnectedViaLabel(connectedUser);
                            if (!viaLabel) return null;
                            const viaKey = connectedUser?.connectedVia ?? connectedUser?.connected_via;
                            return (
                              <span className={`px-2 py-1 inline-flex items-center text-xs font-semibold rounded-full border ${getConnectedViaBadge(viaKey)}`}>
                                <FiWifi className="w-3 h-3 mr-1" />
                                {viaLabel}
                              </span>
                            );
                          })()}
                        {user.is_banned && (
                            <span className="px-2 py-1 inline-flex text-xs font-semibold rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                            Banni
                          </span>
                        )}
                      </div>
                    </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/admin/users/${user.id}`}
                              className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg border border-blue-500/30 transition-all flex items-center"
                          title="Voir détails"
                        >
                          <FiEye className="mr-1" />
                          Voir
                        </Link>
                        {(user.role === 'demenageur' || user.role === 'mover') && (
                          <button
                            type="button"
                            onClick={() => openMissionCreditsModal(user)}
                            className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg border border-emerald-500/30 transition-all flex items-center"
                            title="Ajouter des crédits mission au solde"
                          >
                            <FiTrendingUp className="mr-1" />
                            Crédits
                          </button>
                        )}
                        {user.is_banned ? (
                          <button
                            onClick={() => handleUnbanUser(user.id)}
                                className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg border border-green-500/30 transition-all flex items-center"
                            title="Débannir"
                          >
                            <FiUnlock className="mr-1" />
                            Débannir
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBanUser(user.id)}
                            disabled={user.is_admin}
                                className="px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg border border-orange-500/30 transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            title={user.is_admin ? 'Impossible de bannir un administrateur' : 'Bannir'}
                          >
                            <FiUserX className="mr-1" />
                            Bannir
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUsers([user.id])}
                              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg border border-red-500/30 transition-all flex items-center"
                          title="Supprimer"
                        >
                          <FiTrash2 className="mr-1" />
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>

          {/* Pagination améliorée */}
        {totalPages > 1 && (
            <div className="bg-gray-900/50 px-6 py-4 flex items-center justify-between border-t border-gray-700">
            <div className="text-sm text-gray-400">
                Affichage de <span className="font-semibold text-white">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                <span className="font-semibold text-white">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> sur{' '}
                <span className="font-semibold text-white">{filteredUsers.length}</span> utilisateurs
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-medium bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>
                <span className="px-4 py-2 text-sm text-gray-400">
                  Page {currentPage} / {totalPages}
                </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-sm font-medium bg-gray-800 hover:bg-gray-700 hover:border-yellow-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
      ) : (
        // Vue Grille
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-700 border-t-yellow-500"></div>
            </div>
          ) : paginatedUsers.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FiUser className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 font-medium">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            paginatedUsers.map((user) => {
              const connectedUser = connectedUsers.find((cu) => String(cu.id) === String(user.id));
              const isOnline = connectedUser?.isOnline || false;
              
              return (
                <div
                  key={user.id}
                  className={`bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-5 shadow-xl hover:shadow-2xl transition-all ${
                    selectedUsers.includes(user.id) ? 'ring-2 ring-yellow-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 border-2 border-gray-800 flex items-center justify-center text-white font-bold shadow-lg">
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-bold text-white">
                          {user.first_name} {user.last_name}
                        </h3>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="w-5 h-5 text-yellow-600 border-gray-600 rounded focus:ring-yellow-500 bg-gray-800 cursor-pointer"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    {user.phone && (
                      <div className="flex items-center text-xs text-gray-400">
                        <FiPhone className="mr-2" />
                        {user.phone}
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-1">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadge(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                      {(user.role === 'demenageur' || user.role === 'mover') && (() => {
                        const serviceType = getMoverServiceType(user, connectedUser);
                        const serviceLabel = getMoverServiceTypeLabel(serviceType);
                        if (!serviceLabel) return null;
                        return (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getMoverServiceTypeBadge(serviceType)}`}>
                            {serviceLabel}
                          </span>
                        );
                      })()}
                      {isOnline ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                          En ligne
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30 flex items-center">
                          <FiWifiOff className="w-3 h-3 mr-1" />
                          Hors ligne
                        </span>
                      )}
                      {isOnline && (() => {
                        const viaLabel = getConnectedViaLabel(connectedUser);
                        if (!viaLabel) return null;
                        const viaKey = connectedUser?.connectedVia ?? connectedUser?.connected_via;
                        return (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full border flex items-center ${getConnectedViaBadge(viaKey)}`}>
                            {viaLabel}
                          </span>
                        );
                      })()}
                    </div>

                    {(user.role === 'demenageur' || user.role === 'mover') && (
                      <div className="text-xs space-y-1 py-2 border-t border-gray-700/50">
                        <p className="text-emerald-400">
                          Solde missions :{' '}
                          <span className="text-white font-semibold">{missionCreditsForUser(user)}</span>
                        </p>
                        <p className="text-gray-400">
                          Missions terminées :{' '}
                          <span className="text-gray-200">{missionsCountForUser(user)}</span>
                        </p>
                      </div>
                    )}
                    
                    {user.is_banned && (
                      <div className="px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-xs font-semibold text-center">
                        Banni
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 flex items-center">
                      <FiCalendar className="mr-1" />
                      {formatRelativeDate(user.created_at)}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="flex-1 min-w-[4rem] px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg border border-blue-500/30 transition-all text-xs font-semibold text-center"
                      >
                        Voir
                      </Link>
                      {(user.role === 'demenageur' || user.role === 'mover') && (
                        <button
                          type="button"
                          onClick={() => openMissionCreditsModal(user)}
                          className="flex-1 min-w-[4rem] px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg border border-emerald-500/30 transition-all text-xs font-semibold"
                          title="Ajouter des crédits mission"
                        >
                          Crédits
                        </button>
                      )}
                      {user.is_banned ? (
                        <button
                          onClick={() => handleUnbanUser(user.id)}
                          className="flex-1 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg border border-green-500/30 transition-all text-xs font-semibold"
                        >
                          Débannir
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBanUser(user.id)}
                          disabled={user.is_admin}
                          className="flex-1 px-3 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg border border-orange-500/30 transition-all text-xs font-semibold disabled:opacity-50"
                        >
                          Bannir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadUsers}
      />

      {showMissionCreditsModal && missionCreditsUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-emerald-600/30 rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-emerald-600/30 px-6 py-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-emerald-400 flex items-center">
                <FiTrendingUp className="w-6 h-6 mr-3 shrink-0" />
                Crédits mission
              </h2>
              <button
                type="button"
                onClick={closeMissionCreditsModal}
                disabled={missionCreditsLoading}
                className="text-gray-400 hover:text-emerald-400 transition-colors p-2 hover:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-300">
                Ajouter au solde de{' '}
                <span className="font-semibold text-white">
                  {missionCreditsUser.first_name} {missionCreditsUser.last_name}
                </span>{' '}
                (solde actuel :{' '}
                <span className="text-emerald-400 font-semibold">{missionCreditsForUser(missionCreditsUser)}</span>).
              </p>
              <div>
                <label htmlFor="mission-credits-amount" className="block text-sm font-semibold text-gray-300 mb-2">
                  Nombre de crédits à ajouter (1–999)
                </label>
                <input
                  id="mission-credits-amount"
                  type="number"
                  min={1}
                  max={999}
                  value={missionCreditsAmount}
                  onChange={(e) => setMissionCreditsAmount(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                Les crédits augmentent le solde utilisable pour les missions payantes côté déménageur.
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeMissionCreditsModal}
                  disabled={missionCreditsLoading}
                  className="flex-1 px-4 py-2.5 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={confirmAddMissionCredits}
                  disabled={missionCreditsLoading}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  {missionCreditsLoading ? 'Envoi…' : 'Ajouter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal amélioré */}
      {showBanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-orange-600/30 rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-orange-600/30 px-6 py-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-orange-400 flex items-center">
                <FiUserX className="w-6 h-6 mr-3" />
                Bannir l'utilisateur
              </h2>
              <button 
                onClick={() => {
                  setShowBanModal(false);
                  setBanReason('');
                  setBanUserId(null);
                }} 
                className="text-gray-400 hover:text-orange-400 transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="p-4 bg-orange-500/20 border border-orange-500/50 rounded-lg">
                <p className="text-sm text-orange-300 flex items-center">
                  <FiAlertCircle className="w-4 h-4 inline mr-2" />
                  Cette action empêchera l'utilisateur de se connecter à la plateforme.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Raison du bannissement (optionnel)
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Entrez la raison du bannissement..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 placeholder-gray-500 resize-none transition-all"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={confirmBan}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-500 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center shadow-lg"
                >
                  {loading ? 'Bannissement...' : 'Confirmer le bannissement'}
                </button>
                <button
                  onClick={() => {
                    setShowBanModal(false);
                    setBanReason('');
                    setBanUserId(null);
                  }}
                  className="px-6 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Modal */}
      {showBulkActionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 px-6 py-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-yellow-400 flex items-center">
                <FiSettings className="w-6 h-6 mr-3" />
                Actions en masse ({selectedUsers.length} utilisateurs)
              </h2>
              <button 
                onClick={() => setShowBulkActionsModal(false)} 
                className="text-gray-400 hover:text-yellow-400 transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleBulkBan}
                  disabled={loading}
                  className="px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-500 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                >
                  <FiUserX className="mr-2" />
                  Bannir
                </button>
                <button
                  onClick={handleBulkUnban}
                  disabled={loading}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                >
                  <FiUnlock className="mr-2" />
                  Débannir
                </button>
              </div>
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
              >
                <FiTrash2 className="mr-2" />
                Supprimer
              </button>
              <button
                onClick={() => setShowBulkActionsModal(false)}
                className="w-full px-4 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmModalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 px-6 py-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-100 flex items-center">
                {confirmModalConfig.icon && (
                  <span className="mr-3">{confirmModalConfig.icon}</span>
                )}
                {confirmModalConfig.title}
              </h2>
              <button 
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmModalConfig(null);
                }} 
                className="text-gray-400 hover:text-gray-200 transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
                <p className="text-gray-300 text-center">
                  {confirmModalConfig.message}
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmModalConfig(null);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  {confirmModalConfig.cancelText || 'Annuler'}
                </button>
                <button
                  onClick={confirmModalConfig.onConfirm}
                  disabled={loading}
                  className={`flex-1 px-6 py-3 text-white rounded-lg transition-colors font-semibold disabled:opacity-50 flex items-center justify-center shadow-lg ${
                    confirmModalConfig.confirmColor === 'red' 
                      ? 'bg-red-600 hover:bg-red-500' 
                      : confirmModalConfig.confirmColor === 'green'
                      ? 'bg-green-600 hover:bg-green-500'
                      : confirmModalConfig.confirmColor === 'orange'
                      ? 'bg-orange-600 hover:bg-orange-500'
                      : 'bg-yellow-600 hover:bg-yellow-500'
                  }`}
                >
                  {loading ? 'Traitement...' : confirmModalConfig.confirmText || 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
